import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Switch,
  Button,
  Box,
  Typography,
  Stack
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const initialDocumentState = {
  name: '',
  description: '',
  internal_name: '',
  for_user: 'both',
  entry_type: 'file',
  entry_name: 'File Only',
  max_files: 1,
  max_size: 5,
  allowed_format_type: [
    "jpg",
    "png",
    "pdf"
  ],
  requested: false,
  state: true,
  required: true
};

const CreateDocumentDialog = ({ open, onClose, onSubmit, editDocument }) => {
  const { t } = useTranslation();
  const [newDocument, setNewDocument] = useState(editDocument || initialDocumentState);
  const [errors, setErrors] = useState({});

  // Reset form when dialog opens/closes or editDocument changes
  React.useEffect(() => {
    if (open) {
      setNewDocument(editDocument || initialDocumentState);
      setErrors({});
    }
  }, [open, editDocument]);

  const validateForm = () => {
    const newErrors = {};

    if (!newDocument.name.trim()) {
      newErrors.name = 'Document name is required';
    }
    if (!newDocument.internal_name.trim()) {
      newErrors.internal_name = 'Internal name is required';
    }

    // Add validation for internalName format
    if (newDocument.internal_name) {
      const internalNameRegex = /^[a-zA-Z0-9_]+$/;
      if (!internalNameRegex.test(newDocument.internal_name)) {
        newErrors.internal_name = t('internal_name_format_error') || 'Only letters, numbers, and underscores are allowed';
      }
    }

    if (!newDocument.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (newDocument.max_files < 1) {
      newErrors.max_files = 'Maximum files must be at least 1';
    }
    if (newDocument.max_size < 1) {
      newErrors.max_size = 'Maximum size must be at least 1MB';
    }
    if (newDocument.allowed_format_type.length === 0) {
      newErrors.allowed_format_type = 'At least one format must be selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setNewDocument(prev => ({
      ...prev,
      [name]: name === 'max_files' || name === 'max_size'
        ? parseFloat(value) || 0
        : value,
      // Update field_type when entry_type changes
      ...(name === 'entry_type' && {
        entry_name: value === 'file' ? 'File Only' : 'Text/Number + File'
      })
    }));
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleFormatChange = (format) => {
    setNewDocument(prev => ({
      ...prev,
      allowed_format_type: prev.allowed_format_type.includes(format)
        ? prev.allowed_format_type.filter(f => f !== format)
        : [...prev.allowed_format_type, format]
    }));
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(newDocument, !!editDocument);
      setNewDocument(initialDocumentState);
      setErrors({});
      onClose();
    }
  };

  const handleClose = () => {
    setNewDocument(initialDocumentState);
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editDocument ? 'Edit Document Requirement' : 'Create New Document Requirement'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label={t("document_name")}
            name="name"
            value={newDocument.name}
            onChange={handleChange}
            placeholder={t("ex_license_to_drive")}
            error={!!errors.name}
            helperText={errors.name}
            required
          />

          <TextField
            fullWidth
            label={t("internal_name")}
            name="internal_name"
            value={newDocument.internal_name}
            onChange={handleChange}
            placeholder={t("ex_drivers_license")}
            helperText={errors.internal_name || t("unique_identifier_without_spaces")}
            error={!!errors.internal_name}
            required
          />

          <TextField
            fullWidth
            multiline
            rows={2}
            label={t("description")}
            name="description"
            value={newDocument.description}
            onChange={handleChange}
            placeholder={t("brief_description_of_the_document")}
            error={!!errors.description}
            helperText={errors.description}
            required
          />

          <FormControl fullWidth>
            <InputLabel>{t("user_type")}</InputLabel>
            <Select
              name="for_user"
              value={newDocument.for_user}
              onChange={handleChange}
              label={t("user_type")}
            >
              <MenuItem value="both">{t("both")}</MenuItem>
              <MenuItem value="driver">{t("driver")}</MenuItem>
              <MenuItem value="customer">{t("customer")}</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>{t("entry_type")}</InputLabel>
            <Select
              name="entry_type"
              value={newDocument.entry_type}
              onChange={handleChange}
              label={t("entry_type")}
            >
              <MenuItem value="file">{t("file_only")}</MenuItem>
              <MenuItem value="text_file">{t("text_number_file")}</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t("file_configuration")}
            </Typography>

            <Stack spacing={2}>
              <TextField
                type="number"
                label={t("maximum_number_of_files")}
                name="max_files"
                value={newDocument.max_files}
                onChange={handleChange}
                inputProps={{ min: 1 }}
                error={!!errors.max_files}
                helperText={errors.max_files}
                required
              />

              <TextField
                type="number"
                label={t("maximum_size")}
                name="max_size"
                value={newDocument.max_size}
                onChange={handleChange}
                inputProps={{ min: 1 }}
                error={!!errors.max_size}
                helperText={errors.max_size}
                required
              />

              <Box>
                <Typography variant="body2" gutterBottom>
                  {t("allowed_formats")}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={newDocument.allowed_format_type.includes('jpg')}
                        onChange={() => handleFormatChange('jpg')}
                      />
                    }
                    label="JPG"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={newDocument.allowed_format_type.includes('png')}
                        onChange={() => handleFormatChange('png')}
                      />
                    }
                    label="PNG"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={newDocument.allowed_format_type.includes('pdf')}
                        onChange={() => handleFormatChange('pdf')}
                      />
                    }
                    label="PDF"
                  />
                </Box>
              </Box>
            </Stack>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={newDocument.required}
                onChange={(e) => setNewDocument(prev => ({
                  ...prev,
                  required: e.target.checked
                }))}
              />
            }
            label={t("required_document")}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t("cancel")}</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          {editDocument ? t("save_changes") : t("create_document")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateDocumentDialog; 